import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Microphone, Quotes } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import PageMeta from "@/components/PageMeta";

const SITE = "https://recipefiler.com";

function schemas(c) {
  return [
    {
      "@context": "https://schema.org", "@type": "WebPage", name: c.h1, url: `${SITE}/voice`,
      description: c.seo?.seo_description,
      speakable: { "@type": "SpeakableSpecification", cssSelector: ["#voice-speakable-summary", ".voice-section-body"] },
    },
    {
      "@context": "https://schema.org", "@type": "HowTo", name: c.howto_title,
      step: (c.howto_steps || []).map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title, text: s.body })),
    },
    {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: (c.faqs || []).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Voice", item: `${SITE}/voice` },
      ],
    },
  ];
}

export default function VoicePage() {
  const [c, setC] = useState(null);
  const { limits } = useSiteFlags();
  useEffect(() => { api.get("/public/site-content/voice").then((r) => setC(r.data.content)).catch(() => setC({})); }, []);
  if (!c) return <div className="min-h-[60vh]" data-testid="voice-page-loading" aria-busy="true" />;
  const free = (s) => (s || "").replaceAll("{FREE}", String(limits?.free_recipe_limit ?? 10));

  return (
    <article className="max-w-5xl mx-auto px-6 py-16" data-testid="voice-page">
      <PageMeta title={c.seo?.seo_title} description={c.seo?.seo_description} keywords={c.seo?.seo_keywords} canonical={`${SITE}/voice`} jsonLd={schemas(c)} />
      <header className="mb-14 max-w-3xl">
        <p className="text-xs uppercase tracking-[0.2em] font-black text-rf-orange mb-3">{c.overline}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-rf-ink leading-[1.05] mb-5" data-testid="voice-page-h1">{c.h1}</h1>
        <p className="text-base md:text-lg text-rf-ink-soft leading-relaxed mb-4">{c.intro}</p>
        <p id="voice-speakable-summary" className="text-sm text-rf-ink border-l-4 border-rf-amber pl-4">{c.speakable_summary}</p>
      </header>

      <section aria-labelledby="voice-howto" className="mb-16">
        <h2 id="voice-howto" className="font-display text-2xl font-black text-rf-ink mb-6">{c.howto_title}</h2>
        <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {(c.howto_steps || []).map((s, i) => (
            <li key={s.title} className="bg-white rounded-2xl border border-rf-ink/10 p-5" data-testid={`voice-howto-step-${i}`}>
              <span className="font-display text-3xl font-black text-rf-orange">{i + 1}</span>
              <h3 className="font-bold text-rf-ink mt-2 mb-1">{s.title}</h3>
              <p className="text-sm text-rf-ink-soft">{s.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <div className="grid md:grid-cols-2 gap-6 mb-16">
        {(c.sections || []).map((s, i) => (
          <section key={s.title} aria-labelledby={`voice-sec-${i}`} className="bg-rf-cream-alt rounded-2xl p-6" data-testid={`voice-section-${i}`}>
            <h2 id={`voice-sec-${i}`} className="font-display text-xl font-black text-rf-ink mb-2 flex items-center gap-2">
              <Microphone size={18} weight="duotone" className="text-rf-orange" aria-hidden="true" /> {s.title}
            </h2>
            <p className="voice-section-body text-sm text-rf-ink-soft mb-3">{s.body}</p>
            <ul className="space-y-1.5">
              {(s.examples || "").split("\n").filter(Boolean).map((ex) => (
                <li key={ex} className="text-sm font-semibold text-rf-ink flex gap-2">
                  <Quotes size={14} weight="fill" className="text-rf-amber shrink-0 mt-0.5" aria-hidden="true" /> {ex}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <section aria-labelledby="voice-faq" className="mb-16">
        <h2 id="voice-faq" className="font-display text-2xl font-black text-rf-ink mb-6">Voice FAQs</h2>
        <dl className="space-y-4">
          {(c.faqs || []).map((f) => (
            <div key={f.q} className="bg-white rounded-xl border border-rf-ink/10 p-5">
              <dt className="font-bold text-rf-ink mb-1">{f.q}</dt>
              <dd className="text-sm text-rf-ink-soft">{f.a}</dd>
            </div>
          ))}
        </dl>
        <p className="text-sm mt-4"><Link to="/faqs" className="text-rf-orange font-bold underline">More questions in our FAQs</Link></p>
      </section>

      <section className="bg-rf-ink text-rf-cream rounded-3xl p-10 text-center">
        <h2 className="font-display text-3xl font-black mb-2">{c.cta_h2}</h2>
        <p className="mb-6 opacity-80">{free(c.cta_body)}</p>
        <Link to="/register" className="btn-amber" data-testid="voice-page-cta">{c.cta_button_label}</Link>
      </section>
    </article>
  );
}
